import { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useUser } from '../context/UserContext';
import { apiCall } from '../api/client';
import AudioButton from '../components/AudioButton';
import logo from '../components/BeeFluent logos/beefluent_logo_512.png';
import { MASTERED_INTERVAL } from '../constants';

function isAudio(columnTypes, index) {
  return ((Array.isArray(columnTypes) && columnTypes[index]) || 'text') === 'audio';
}

// The course's single audio column index (a course has at most one), or null.
function audioColumnIndex(columnTypes) {
  if (!Array.isArray(columnTypes)) return null;
  const i = columnTypes.findIndex(t => t === 'audio');
  return i === -1 ? null : i;
}

// Makes each card carry its audio-column value under values[audioCol], so the clip
// can play even when the audio column is neither the level's source nor target.
// Source/target audio is already on the card; a separate audio column isn't, so we
// use the URL stored on the entry (entry.audio). Entries created before audio was
// persisted have no entry.audio — for those we fall back to recovering it from the
// database entries (which hold every column) by matching on source+target. Once a
// course is backfilled, every entry has entry.audio and no matching happens at all.
function attachAudio(resolved, audioCol, srcCol, tgtCol, dbEntries) {
  if (audioCol == null || audioCol === srcCol || audioCol === tgtCol) return;

  // Build the source+target → audio lookup only if some entry still lacks its URL.
  const needsMatch = resolved.some(c => !c.entry?.audio);
  const key = (src, tgt) => JSON.stringify([src ?? '', tgt ?? '']);
  const byKey = new Map();
  if (needsMatch) {
    for (const e of dbEntries || []) {
      if (!Array.isArray(e.values)) continue;
      const k = key(e.values[srcCol], e.values[tgtCol]);
      if (!byKey.has(k)) byKey.set(k, e.values[audioCol] || null);
    }
  }

  for (const card of resolved) {
    const url = card.entry?.audio || byKey.get(key(card.values[srcCol], card.values[tgtCol]));
    if (url) card.values[audioCol] = url;
  }
}

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function getMCOptions(correctValue, allEntries, targetCol) {
  // Dedupe distractors so two entries with the same value can't yield
  // duplicate (or correct-looking) options.
  const pool = [...new Set(allEntries
    .map(e => e.values[targetCol])
    .filter(v => v && v !== correctValue))];
  return shuffle([correctValue, ...shuffle(pool).slice(0, 3)]);
}

// Routed at /courses/:courseId/study/:mode (mode = learn | review), with an
// optional ?level=<levelId> to scope the session to one level. Without a level,
// review covers the whole course and learn picks the first level that still has
// unseen words (matching the course page's Learn button).
export default function StudySession() {
  const { courseId, mode } = useParams();
  const [searchParams] = useSearchParams();
  const levelId = searchParams.get('level');
  const navigate = useNavigate();
  const { token, isGuest } = useAuth();
  // Session lengths come from the user's saved preferences (fetched once at
  // sign-in and shared via context). Guests have no account → the defaults.
  const { user } = useUser();
  const learnSize = user?.settings?.learnSessionSize ?? 6;
  const reviewSize = user?.settings?.reviewSessionSize ?? 20;

  const onBack = () => navigate(`/courses/${courseId}`);
  const [loading, setLoading] = useState(true);
  const [queue, setQueue] = useState([]);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [allEntries, setAllEntries] = useState([]);
  const [columns, setColumns] = useState([]);
  const [mcOptions, setMcOptions] = useState([]);
  const [selectedMC, setSelectedMC] = useState(null);
  const [typingInput, setTypingInput] = useState('');
  const [typingChecked, setTypingChecked] = useState(false);
  const [typingCorrect, setTypingCorrect] = useState(false);
  const [stats, setStats] = useState({ correct: 0, wrong: 0 });
  const typingInputRef = useRef(null);
  const playerRef = useRef(null);

  // Plays an audio clip programmatically (e.g. when an answer is committed),
  // stopping any clip already playing. Ignored when there's no url / playback fails.
  function playClip(url) {
    if (!url) return;
    try {
      if (playerRef.current) playerRef.current.pause();
      const audio = new Audio(url);
      playerRef.current = audio;
      audio.play().catch(() => {});
    } catch { /* ignore */ }
  }

  // The word's audio-column clip (or null). Covers all cases: when the audio column
  // is the source or target its value is on the card directly; when it's a separate
  // column, attachAudio has copied the value onto the card under values[audioCol].
  function taskAudioUrl(t) {
    if (!t || t.audioCol == null) return null;
    return t.word.values[t.audioCol] || null;
  }

  // Shared: fetches course, level, database entries, and resolved cards
  async function loadCourseData(courseId, levelId, token) {
    const courseData = await apiCall('GET', `/courses/${courseId}`, null, token);
    const targetLevel = courseData.levels.find(l => l.id === levelId);

    let dbEntries = [];
    let cols = [];
    let colTypes = [];
    if (courseData.databaseId) {
      const db = await apiCall('GET', `/databases/${courseData.databaseId}`, null, token);
      dbEntries = db.entries || [];
      cols = db.columns || [];
      colTypes = db.columnTypes || [];
    } else {
      // Custom courses store their columns on the course itself (older ones only
      // have the legacy source/target name pair).
      cols = courseData.columns || [courseData.sourceColumnName, courseData.targetColumnName].filter(Boolean);
      colTypes = courseData.columnTypes || [];
    }

    const { cards } = await apiCall('GET', `/study/all/${courseId}/${levelId}`, null, token);
    const srcCol = targetLevel.sourceColumn;
    const tgtCol = targetLevel.targetColumn;
    const resolved = cards.map(card => ({
      ...card,
      values: card.entry ? {
        ...card.entry,
        [srcCol]: card.entry.source,
        [tgtCol]: card.entry.target
      } : null
    })).filter(c => c.values);

    return { courseData, targetLevel, dbEntries, cols, colTypes, resolved };
  }

  // Learn: builds intro → MC → (writing | MC) tasks for new words. The final
  // scoring round — which marks a word as learned — is writing for text targets,
  // but multiple choice for audio targets, since you can't type an audio answer.
  function buildLearnQueue(resolved, src, tgt, sessionSize, pool, srcIsAudio, tgtIsAudio, audioCol) {
    const newWords = resolved.filter(c => !c.progress?.ignored && (!c.progress || !c.progress.lastReviewed)).slice(0, sessionSize);
    const tasks = [];
    const base = { src, tgt, pool, srcIsAudio, tgtIsAudio, audioCol };

    for (let i = 0; i < newWords.length; i += 2) {
      const pair = newWords.slice(i, i + 2);
      pair.forEach(w => tasks.push({ type: 'intro_show', word: w, ...base }));
      pair.forEach(w => tasks.push({ type: 'mc', word: w, ...base }));
    }
    shuffle(newWords).forEach(w => tasks.push({ type: 'mc', word: w, ...base }));
    const scoringType = tgtIsAudio ? 'mc' : 'writing';
    shuffle(newWords).forEach(w => tasks.push({ type: scoringType, word: w, ...base, score: true }));

    return tasks;
  }

  // Review: due words first; falls back to all learned words if nothing is due.
  // Audio targets use multiple choice (writing is impossible); text uses writing.
  function buildReviewQueue(resolved, src, tgt, sessionSize, pool, srcIsAudio, tgtIsAudio, audioCol) {
    const now = new Date();
    const due = resolved.filter(c => {
      const p = c.progress;
      return p && !p.ignored && p.lastReviewed && new Date(p.nextDue) <= now && (p.interval || 0) < MASTERED_INTERVAL;
    });
    const isDue = due.length > 0;
    const words = isDue
      ? due
      : resolved.filter(c => c.progress?.lastReviewed && !c.progress?.ignored);
    const type = tgtIsAudio ? 'mc' : 'writing';
    return shuffle(words).slice(0, sessionSize).map(w => ({ type, word: w, src, tgt, pool, srcIsAudio, tgtIsAudio, audioCol, isDue }));
  }

  // Initialize session: load course data and build task queue
  useEffect(() => {
    async function init() {
      try {
        if (!levelId) {
          // Course-level session: one course-wide cards request, grouped by level.
          const courseData = await apiCall('GET', `/courses/${courseId}`, null, token);
          let dbCols = [];
          let colTypes = [];
          let dbEntries = [];
          if (courseData.databaseId) {
            const db = await apiCall('GET', `/databases/${courseData.databaseId}`, null, token);
            dbCols = db.columns || [];
            colTypes = db.columnTypes || [];
            dbEntries = db.entries || [];
          } else {
            dbCols = courseData.columns || [courseData.sourceColumnName, courseData.targetColumnName].filter(Boolean);
            colTypes = courseData.columnTypes || [];
          }
          setColumns(dbCols);
          const audioCol = audioColumnIndex(colTypes);

          const { cards } = await apiCall('GET', `/study/all/${courseId}`, null, token);
          const cardsByLevel = new Map();
          for (const card of cards) {
            if (!cardsByLevel.has(card.levelId)) cardsByLevel.set(card.levelId, []);
            cardsByLevel.get(card.levelId).push(card);
          }

          // Only drill levels carry cards.
          const drillLevels = courseData.levels.filter(l => (l.type || 'drill') === 'drill');
          const resolvedByLevel = new Map();
          const allResolved = [];
          for (const level of drillLevels) {
            const srcCol = level.sourceColumn;
            const tgtCol = level.targetColumn;
            const resolved = (cardsByLevel.get(level.id) || []).map(card => ({
              ...card,
              values: card.entry ? {
                ...card.entry,
                [srcCol]: card.entry.source,
                [tgtCol]: card.entry.target
              } : null,
              _levelId: level.id,
              _levelSrc: srcCol,
              _levelTgt: tgtCol,
              _srcIsAudio: isAudio(colTypes, srcCol),
              _tgtIsAudio: isAudio(colTypes, tgtCol),
            })).filter(c => c.values);
            // Recover the audio-column clip onto each card when audio is a separate
            // column (matched per-level since each level maps its own src/tgt columns).
            attachAudio(resolved, audioCol, srcCol, tgtCol, dbEntries);
            // Distractor pool for audio multiple choice: the level's own words.
            resolved.forEach(r => { r._pool = resolved; });
            resolvedByLevel.set(level.id, resolved);
            allResolved.push(...resolved);
          }

          if (mode === 'learn') {
            // Course-level learn: the first level (in course order) that still has
            // unseen words — the same level the course page's Learn button targets.
            const nextLevel = drillLevels.find(l => (resolvedByLevel.get(l.id) || []).some(
              c => !c.progress?.ignored && (!c.progress || !c.progress.lastReviewed)
            ));
            if (nextLevel) {
              const resolved = resolvedByLevel.get(nextLevel.id);
              const pool = dbEntries.length ? dbEntries : resolved;
              setAllEntries(pool);
              setQueue(buildLearnQueue(
                resolved, nextLevel.sourceColumn, nextLevel.targetColumn, learnSize, pool,
                isAudio(colTypes, nextLevel.sourceColumn), isAudio(colTypes, nextLevel.targetColumn), audioCol
              ));
            }
            setLoading(false);
            return;
          }

          const now = new Date();
          const dueWords = allResolved.filter(c => {
            const p = c.progress;
            return p && !p.ignored && p.lastReviewed && new Date(p.nextDue) <= now && (p.interval || 0) < MASTERED_INTERVAL;
          });
          const isDue = dueWords.length > 0;
          const wordsToReview = isDue
            ? dueWords
            : allResolved.filter(c => c.progress?.lastReviewed && !c.progress?.ignored);
          setQueue(shuffle(wordsToReview).slice(0, reviewSize).map(w => ({
            type: w._tgtIsAudio ? 'mc' : 'writing',
            word: w,
            src: w._levelSrc,
            tgt: w._levelTgt,
            pool: w._pool,
            srcIsAudio: w._srcIsAudio,
            tgtIsAudio: w._tgtIsAudio,
            audioCol,
            isDue,
          })));
          setLoading(false);
          return;
        }

        const { targetLevel, dbEntries, cols, colTypes, resolved } = await loadCourseData(courseId, levelId, token);

        // Multiple-choice distractors come from the database when there is one;
        // custom courses have no database, so fall back to the level's own words.
        // Both shapes expose values[columnIndex], which is all getMCOptions reads.
        const pool = dbEntries.length ? dbEntries : resolved;
        setAllEntries(pool);
        setColumns(cols);

        const srcCol = targetLevel.sourceColumn;
        const tgtCol = targetLevel.targetColumn;
        const srcIsAudio = isAudio(colTypes, srcCol);
        const tgtIsAudio = isAudio(colTypes, tgtCol);
        const audioCol = audioColumnIndex(colTypes);
        // Recover the audio-column clip onto each card when audio is a separate column.
        attachAudio(resolved, audioCol, srcCol, tgtCol, dbEntries);

        const tasks = mode === 'learn'
          ? buildLearnQueue(resolved, srcCol, tgtCol, learnSize, pool, srcIsAudio, tgtIsAudio, audioCol)
          : buildReviewQueue(resolved, srcCol, tgtCol, reviewSize, pool, srcIsAudio, tgtIsAudio, audioCol);

        setQueue(tasks);
        setLoading(false);
      } catch (err) {
        console.error('Failed to initialize session:', err);
        setLoading(false); // Stop loading even on error
      }
    }

    init();
    // learnSize/reviewSize are primitives, so this only re-runs if the actual
    // preference values change — impossible mid-session (Settings is unmounted).
  }, [courseId, levelId, token, mode, learnSize, reviewSize]);

  const task = queue[currentIdx];

  // Reset state when task changes. Layout effect so MC options/selection clear before paint —
  // prevents a one-frame flash of the previous answer's highlight and Continue button when
  // moving between two multiple-choice questions.
  useLayoutEffect(() => {
    if (!task) return;
    if (task.type === 'mc') {
      setMcOptions(getMCOptions(task.word.values[task.tgt], task.pool || allEntries, task.tgt));
      setSelectedMC(null);
    }
    if (task.type === 'writing') {
      setTypingInput('');
      setTypingChecked(false);
      setTypingCorrect(false);
    }
  }, [currentIdx, queue]);

  // Focus the writing input so the user can type immediately on every question, not just the
  // first. autoFocus fires only on the input's first mount, and the reset above can't focus
  // directly: when it runs the input is still rendered disabled from the previous answer
  // (typingChecked hasn't re-rendered to false yet), and a disabled element can't take focus.
  // Keying on typingChecked re-runs this once the reset re-enables the input.
  useLayoutEffect(() => {
    if (task?.type === 'writing' && !typingChecked) {
      typingInputRef.current?.focus();
    }
  }, [currentIdx, queue, typingChecked, task]);

  // Keyboard shortcuts for multiple choice: press 1-4 to select an option
  useEffect(() => {
    if (!task || task.type !== 'mc' || selectedMC) return;
    function handleKey(e) {
      const num = parseInt(e.key, 10);
      if (num >= 1 && num <= mcOptions.length) {
        handleMCSelect(mcOptions[num - 1]);
      }
    }
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [task, mcOptions, selectedMC]);

  // Keyboard shortcut: press Enter to advance — through intro cards and checked written answers.
  // The listener is attached on the next tick so the same Enter keypress that grades a written
  // answer (handled by the input's onKeyDown) can't bubble up to window and advance in one press.
  useEffect(() => {
    if (!task) return;
    let advance = null;
    if (task.type === 'intro_show') advance = nextTask;
    else if (task.type === 'writing' && typingChecked) advance = handleTypingNext;
    else if (task.type === 'mc' && selectedMC) advance = handleMCProceed;
    if (!advance) return;
    function handleKey(e) {
      if (e.key === 'Enter' && !e.repeat) advance();
    }
    const timer = setTimeout(() => window.addEventListener('keydown', handleKey), 0);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('keydown', handleKey);
    };
  }, [task, typingChecked, typingCorrect, selectedMC]);

  function nextTask() {
    setCurrentIdx(i => i + 1);
  }

  // Records a spaced-repetition grade for a graded task. A task is graded when it's
  // a due review (`isDue`) or the learn session's final scoring round (`score`).
  // Correct → grade 4; a wrong answer on a due review → grade 0 (resets progress).
  async function submitGrade(t, isCorrect) {
    if (isGuest) return;
    let grade = null;
    if (isCorrect && (t.isDue || t.score)) grade = 4;
    else if (!isCorrect && t.isDue) grade = 0;
    if (grade === null) return;
    try {
      await apiCall('POST', '/study/review', {
        courseId,
        levelId: t.word._levelId || levelId,
        entryId: t.word.entry.id,
        grade,
      }, token);
    } catch (err) {
      console.error(err);
    }
  }

  function handleMCSelect(option) {
    if (selectedMC) return;
    const isCorrect = option === task.word.values[task.tgt];
    setSelectedMC(option);
    setStats(s => isCorrect
      ? { ...s, correct: s.correct + 1 }
      : { ...s, wrong: s.wrong + 1 });
    // Play the word's audio so the learner hears the correct pronunciation.
    playClip(taskAudioUrl(task));
    submitGrade(task, isCorrect);
  }

  // Advance past an answered multiple-choice question. A wrong answer re-queues the
  // word (right after the current one) so it comes back for another attempt.
  function handleMCProceed() {
    if (!selectedMC) return;
    const isCorrect = selectedMC === task.word.values[task.tgt];
    if (!isCorrect) {
      setQueue(q => {
        const newQ = [...q];
        newQ.splice(currentIdx + 1, 0, { ...task });
        return newQ;
      });
    }
    nextTask();
  }

  async function handleTypingCheck() {
    const correct = task.word.values[task.tgt].trim().toLowerCase();
    const answer = typingInput.trim().toLowerCase();
    const isCorrect = answer === correct;
    setTypingChecked(true);
    setTypingCorrect(isCorrect);
    setStats(s => isCorrect
      ? { ...s, correct: s.correct + 1 }
      : { ...s, wrong: s.wrong + 1 });
    // If the prompt is audio, replay the word so the learner hears it on answering.
    playClip(taskAudioUrl(task));
    // A correct learn-scoring/due answer records a pass; a wrong due answer resets
    // progress. Wrong learn answers re-queue (in handleTypingNext) with no penalty.
    await submitGrade(task, isCorrect);
  }

  function handleTypingNext() {
    if (typingCorrect) {
      nextTask();
    } else {
      setQueue(q => {
        const newQ = [...q];
        newQ.splice(currentIdx + 1, 0,
          { type: 'mc', word: task.word, src: task.src, tgt: task.tgt, pool: task.pool, srcIsAudio: task.srcIsAudio, tgtIsAudio: task.tgtIsAudio, audioCol: task.audioCol },
          { ...task }
        );
        return newQ;
      });
      nextTask();
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-amber-50 flex items-center justify-center">
        <p className="text-amber-600 text-lg">Preparing session...</p>
      </div>
    );
  }

  // Session complete
  if (!task || currentIdx >= queue.length) {
    return (
      <div className="min-h-screen bg-amber-50 flex items-center justify-center">
        <div className="text-center bg-white rounded-2xl shadow-md p-10 max-w-md w-full mx-4">
          <img src={logo} alt="BeeFluent logo" className="h-20 w-auto mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-amber-700 mb-2">Session Complete!</h2>
          <p className="text-amber-600 mb-8">{stats.correct} correct · {stats.wrong} wrong</p>
          <button
            onClick={onBack}
            className="bg-amber-500 hover:bg-amber-600 text-white font-semibold px-8 py-3 rounded-xl transition"
          >
            Back to Course
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-amber-50">
      <header className="bg-white border-b border-amber-100 px-4 sm:px-6 py-4 flex items-center justify-between gap-3 sticky top-0 z-10">
        <button onClick={onBack} className="text-amber-500 hover:text-amber-700 text-lg flex-shrink-0">←</button>
        <div className="text-sm text-amber-600 font-medium">
          {currentIdx + 1} / {queue.length}
        </div>
        <div className="text-sm text-amber-600 whitespace-nowrap">
          ✓ {stats.correct} · ✗ {stats.wrong}
        </div>
      </header>

      <div className="max-w-xl mx-auto px-4 sm:px-6 py-8 sm:py-12">

        {/* INTRO SHOW */}
        {task.type === 'intro_show' && (
          <div className="text-center">
            <p className="text-amber-500 text-sm mb-8 uppercase tracking-wide font-medium">New Word</p>
            <div className="space-y-4 mb-10">
              <div className="bg-white rounded-2xl shadow-sm border border-amber-100 p-5 sm:p-6 text-left">
                <p className="text-amber-400 text-xs uppercase tracking-wide mb-1">{columns[task.src]}</p>
                {task.srcIsAudio ? (
                  <div className="mb-4"><AudioButton src={task.word.values[task.src]} autoPlay size="md" /></div>
                ) : (
                  <p className="text-xl sm:text-2xl font-bold text-amber-800 mb-4 break-words">{task.word.values[task.src]}</p>
                )}
                <div className="border-t border-amber-100 pt-4">
                  <p className="text-amber-400 text-xs uppercase tracking-wide mb-1">{columns[task.tgt]}</p>
                  {task.tgtIsAudio ? (
                    <AudioButton src={task.word.values[task.tgt]} autoPlay size="md" />
                  ) : (
                    <p className="text-xl sm:text-2xl font-semibold text-amber-600 break-words">{task.word.values[task.tgt]}</p>
                  )}
                </div>
                {/* Audio that lives in a separate column (neither source nor target):
                    show it here so it auto-plays as the word is introduced. */}
                {task.audioCol != null && task.audioCol !== task.src && task.audioCol !== task.tgt && task.word.values[task.audioCol] && (
                  <div className="border-t border-amber-100 pt-4">
                    <p className="text-amber-400 text-xs uppercase tracking-wide mb-1">{columns[task.audioCol]}</p>
                    <AudioButton src={task.word.values[task.audioCol]} autoPlay size="md" />
                  </div>
                )}
              </div>
            </div>
            <button
              onClick={nextTask}
              className="bg-amber-500 hover:bg-amber-600 text-white font-semibold px-10 py-3 rounded-xl transition"
            >
              Continue →
            </button>
          </div>
        )}

        {/* MULTIPLE CHOICE */}
        {task.type === 'mc' && (
          <div className="text-center">
            <p className="text-amber-500 text-sm mb-6 uppercase tracking-wide font-medium">Multiple Choice</p>
            <div className="bg-white rounded-2xl shadow-sm border border-amber-100 p-6 sm:p-8 mb-8">
              <p className="text-amber-400 text-xs uppercase tracking-wide mb-2">{columns[task.src]}</p>
              {task.srcIsAudio ? (
                <div className="flex justify-center"><AudioButton src={task.word.values[task.src]} autoPlay size="lg" /></div>
              ) : (
                <p className="text-2xl sm:text-3xl font-bold text-amber-800 break-words">{task.word.values[task.src]}</p>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
              {mcOptions.map((option, i) => {
                const correct = task.word.values[task.tgt];
                let style = 'bg-white border-2 border-amber-200 text-amber-800 hover:bg-amber-50 hover:border-amber-400';
                if (selectedMC) {
                  if (option === correct) style = 'bg-green-50 border-2 border-green-400 text-green-800';
                  else if (option === selectedMC) style = 'bg-red-50 border-2 border-red-400 text-red-800';
                  else style = 'bg-white border-2 border-amber-100 text-amber-300';
                }
                // Audio options can't be a single button (a play button can't nest in a
                // select button), so the tile is a row: number, speaker to hear it, and a
                // Pick button to choose it.
                if (task.tgtIsAudio) {
                  return (
                    <div
                      key={i}
                      className={`flex items-center gap-3 p-3 sm:p-4 rounded-xl font-medium transition ${style}`}
                    >
                      <span className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-md border border-current text-xs font-semibold opacity-50">
                        {i + 1}
                      </span>
                      <AudioButton src={option} size="sm" />
                      <button
                        onClick={() => handleMCSelect(option)}
                        disabled={!!selectedMC}
                        className="ml-auto text-sm font-semibold px-4 py-1.5 rounded-lg border-2 border-current disabled:opacity-60"
                      >
                        Pick
                      </button>
                    </div>
                  );
                }
                return (
                  <button
                    key={i}
                    onClick={() => handleMCSelect(option)}
                    className={`flex items-center gap-3 p-3 sm:p-4 rounded-xl font-medium transition text-left ${style}`}
                  >
                    <span className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-md border border-current text-xs font-semibold opacity-50">
                      {i + 1}
                    </span>
                    <span className="break-words min-w-0">{option}</span>
                  </button>
                );
              })}
            </div>
            {selectedMC && (
              <button
                onClick={handleMCProceed}
                className={`mt-8 font-semibold px-10 py-3 rounded-xl transition text-white ${selectedMC === task.word.values[task.tgt]
                    ? 'bg-green-500 hover:bg-green-600'
                    : 'bg-red-500 hover:bg-red-600'
                  }`}
              >
                Continue →
              </button>
            )}
          </div>
        )}

        {/* WRITING */}
        {task.type === 'writing' && (
          <div className="text-center">
            <p className="text-amber-500 text-sm mb-6 uppercase tracking-wide font-medium">Write the Answer</p>
            <div className="bg-white rounded-2xl shadow-sm border border-amber-100 p-6 sm:p-8 mb-6">
              <p className="text-amber-400 text-xs uppercase tracking-wide mb-2">{columns[task.src]}</p>
              {task.srcIsAudio ? (
                <div className="flex justify-center"><AudioButton src={task.word.values[task.src]} autoPlay size="lg" /></div>
              ) : (
                <p className="text-2xl sm:text-3xl font-bold text-amber-800 break-words">{task.word.values[task.src]}</p>
              )}
            </div>
            <p className="text-amber-600 text-sm mb-3 font-medium">{columns[task.tgt]}</p>
            <input
              ref={typingInputRef}
              type="text"
              value={typingInput}
              onChange={e => !typingChecked && setTypingInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && !typingChecked && typingInput && handleTypingCheck()}
              disabled={typingChecked}
              autoFocus
              className={`w-full border-2 rounded-xl px-4 py-3 text-lg text-center focus:outline-none mb-4 transition ${typingChecked
                  ? typingCorrect
                    ? 'border-green-400 bg-green-50 text-green-800'
                    : 'border-red-400 bg-red-50 text-red-800'
                  : 'border-amber-200 focus:border-amber-400 bg-white'
                }`}
              placeholder={`Type in ${columns[task.tgt]}...`}
            />

            {typingChecked && !typingCorrect && (
              <p className="text-red-500 mb-4 text-sm">
                Correct answer: <span className="font-bold">{task.word.values[task.tgt]}</span>
              </p>
            )}

            {!typingChecked ? (
              <button
                onClick={handleTypingCheck}
                disabled={!typingInput}
                className="bg-amber-500 hover:bg-amber-600 disabled:bg-amber-200 disabled:cursor-not-allowed text-white font-semibold px-10 py-3 rounded-xl transition"
              >
                Check
              </button>
            ) : (
              <button
                onClick={handleTypingNext}
                className={`font-semibold px-10 py-3 rounded-xl transition text-white ${typingCorrect
                    ? 'bg-green-500 hover:bg-green-600'
                    : 'bg-red-500 hover:bg-red-600'
                  }`}
              >
                {typingCorrect ? 'Correct! Next →' : 'Got it, continue →'}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}